import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { DEFAULT_SITE_SETTINGS } from "../lib/defaultSiteSettings";
import {
  DEFAULT_THEME_PALETTE_ID,
  getThemePalette,
  hasThemePalette,
} from "../lib/themePalettes";

const SiteSettingsContext = createContext(null);
const THEME_PALETTE_STORAGE_KEY = "x_theme_palette_v1";

function stableSettingsSignature(value) {
  return JSON.stringify(value || null);
}

function deepMerge(base, override) {
  if (override === null || override === undefined) return base;
  if (Array.isArray(override)) return override;
  if (typeof override !== "object") return override;
  const result = { ...(base || {}) };
  for (const key of Object.keys(override)) {
    result[key] = deepMerge(base?.[key], override[key]);
  }
  return result;
}

function resolveAssetUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

function ensureMeta(selector, attrs) {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    document.head.appendChild(node);
  }
  return node;
}

function ensureLink(selector, attrs) {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("link");
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    document.head.appendChild(node);
  }
  return node;
}

function ensureScript(selector, attrs) {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("script");
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    document.head.appendChild(node);
  }
  return node;
}

function toAbsoluteUrl(siteUrl, value) {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:")) return value;
  try {
    return new URL(value, `${siteUrl}/`).toString();
  } catch {
    return value;
  }
}

function routeLabel(pathname) {
  const labels = {
    "/": "Home",
    "/about": "About",
    "/services": "Services",
    "/products": "Products",
    "/quality": "Quality",
    "/compliance": "Compliance",
    "/contact": "Contact",
    "/privacy-policy": "Privacy Policy",
    "/terms-and-conditions": "Terms & Conditions",
    "/cookie-policy": "Cookie Policy",
  };
  return labels[pathname] || "";
}

function getStoredThemePaletteId() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(THEME_PALETTE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistThemePaletteId(paletteId) {
  if (typeof window === "undefined") return;
  try {
    if (!paletteId) {
      window.localStorage.removeItem(THEME_PALETTE_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(THEME_PALETTE_STORAGE_KEY, paletteId);
  } catch {
    // Ignore storage failures and keep runtime behavior intact.
  }
}

function resolvePaletteId(...paletteIds) {
  for (const paletteId of paletteIds) {
    if (hasThemePalette(paletteId)) return paletteId;
  }
  return DEFAULT_THEME_PALETTE_ID;
}

function resolveSettingsWithPalette(baseSettings, ...paletteIds) {
  const themePaletteId = resolvePaletteId(
    ...paletteIds,
    DEFAULT_SITE_SETTINGS?.visual?.theme_palette,
  );
  const paletteTheme = getThemePalette(themePaletteId).theme;

  return deepMerge(baseSettings, {
    visual: {
      theme_palette: themePaletteId,
      theme: paletteTheme,
    },
  });
}

function applyTheme(settings) {
  const theme = settings?.visual?.theme || {};
  const root = document.documentElement;
  const accent = theme.accent || DEFAULT_SITE_SETTINGS.visual.theme.accent;
  root.style.setProperty("--bg-primary", theme.bg_primary || DEFAULT_SITE_SETTINGS.visual.theme.bg_primary);
  root.style.setProperty("--bg-secondary", theme.bg_secondary || DEFAULT_SITE_SETTINGS.visual.theme.bg_secondary);
  root.style.setProperty("--bg-dark", theme.bg_dark || DEFAULT_SITE_SETTINGS.visual.theme.bg_dark);
  root.style.setProperty("--gold", accent);
  root.style.setProperty("--gold-hover", theme.accent_hover || DEFAULT_SITE_SETTINGS.visual.theme.accent_hover);
  root.style.setProperty("--gold-soft", theme.accent_soft || DEFAULT_SITE_SETTINGS.visual.theme.accent_soft);
  root.style.setProperty("--charcoal", theme.interactive_bg || theme.bg_dark || DEFAULT_SITE_SETTINGS.visual.theme.bg_dark);
  root.style.setProperty("--text-primary", theme.text_primary || DEFAULT_SITE_SETTINGS.visual.theme.text_primary);
  root.style.setProperty("--text-secondary", theme.text_secondary || DEFAULT_SITE_SETTINGS.visual.theme.text_secondary);
  root.style.setProperty("--text-muted", theme.text_muted || DEFAULT_SITE_SETTINGS.visual.theme.text_muted);
  root.style.setProperty("--border-light", theme.border_light || DEFAULT_SITE_SETTINGS.visual.theme.border_light);
  root.style.setProperty("--button-text", theme.button_text || DEFAULT_SITE_SETTINGS.visual.theme.button_text);
  root.style.setProperty("--interactive-bg", theme.interactive_bg || DEFAULT_SITE_SETTINGS.visual.theme.interactive_bg);
  root.style.setProperty("--interactive-text", theme.interactive_text || DEFAULT_SITE_SETTINGS.visual.theme.interactive_text);

  const rgb = [
    parseInt(accent.slice(1, 3), 16),
    parseInt(accent.slice(3, 5), 16),
    parseInt(accent.slice(5, 7), 16),
  ];
  root.style.setProperty("--border-gold", `rgba(${rgb.join(", ")}, 0.3)`);
}

function buildStructuredData({
  companyName,
  description,
  image,
  label,
  pathname,
  siteUrl,
  tagline,
  title,
  url,
}) {
  const organizationId = `${siteUrl}#organization`;
  const websiteId = `${siteUrl}#website`;
  const pageId = `${url}#webpage`;
  const graph = [
    {
      "@type": "Organization",
      "@id": organizationId,
      name: companyName,
      url: siteUrl,
      description,
      slogan: tagline,
    },
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: siteUrl,
      name: companyName,
      description,
      publisher: { "@id": organizationId },
      inLanguage: "en",
    },
    {
      "@type": "WebPage",
      "@id": pageId,
      url,
      name: title,
      description,
      isPartOf: { "@id": websiteId },
      about: { "@id": organizationId },
      inLanguage: "en",
    },
  ];

  if (image) {
    graph[0].logo = {
      "@type": "ImageObject",
      url: image,
    };
    graph[2].primaryImageOfPage = {
      "@type": "ImageObject",
      url: image,
    };
  }

  if (pathname === "/") {
    graph[1].potentialAction = {
      "@type": "SearchAction",
      target: `${siteUrl}/products`,
      "query-input": "required name=search_term_string",
    };
  }

  if (label) {
    graph[2].breadcrumb = {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${siteUrl}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: label,
          item: url,
        },
      ],
    };
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

function applyHead(settings, pathname) {
  const seo = settings?.seo || {};
  const defaultFavicon = "/favicon-192x192.png";
  const defaultSocialImage = "/social-preview.png";
  const favicon = resolveAssetUrl(settings?.visual?.favicon?.url) || defaultFavicon;
  const label = routeLabel(pathname);
  const baseTitle = seo.default_title || DEFAULT_SITE_SETTINGS.seo.default_title;
  const title = label && seo.title_template
    ? seo.title_template.replace("%s", label)
    : baseTitle;
  const description = seo.default_description || DEFAULT_SITE_SETTINGS.seo.default_description;
  const siteUrl = (seo.site_url || DEFAULT_SITE_SETTINGS.seo.site_url).replace(/\/$/, "");
  const url = `${siteUrl}${pathname}`;
  const logo =
    resolveAssetUrl(settings?.visual?.logos?.header?.url) ||
    resolveAssetUrl(settings?.visual?.logos?.footer?.url) ||
    favicon;
  const image = resolveAssetUrl(seo.og_image_url) || defaultSocialImage;
  const companyName = seo.company_name || DEFAULT_SITE_SETTINGS.seo.company_name;
  const tagline = seo.tagline || DEFAULT_SITE_SETTINGS.seo.tagline;
  const twitterHandle = seo.twitter_handle || "";
  const isAdminRoute = pathname.startsWith("/admin");
  const robots = isAdminRoute ? "noindex,nofollow" : (seo.robots || DEFAULT_SITE_SETTINGS.seo.robots);
  const absoluteFavicon = toAbsoluteUrl(siteUrl, favicon);
  const absoluteLogo = toAbsoluteUrl(siteUrl, logo);
  const absoluteImage = toAbsoluteUrl(siteUrl, image);
  const structuredData = buildStructuredData({
    companyName,
    description,
    image: absoluteImage || absoluteLogo,
    label,
    pathname,
    siteUrl,
    tagline,
    title,
    url,
  });

  document.title = title;
  document.documentElement.lang = "en";

  ensureMeta('meta[name="description"]', { name: "description" }).setAttribute("content", description);
  ensureMeta('meta[name="keywords"]', { name: "keywords" }).setAttribute("content", seo.default_keywords || DEFAULT_SITE_SETTINGS.seo.default_keywords);
  ensureMeta('meta[name="robots"]', { name: "robots" }).setAttribute("content", robots);
  ensureMeta('meta[name="theme-color"]', { name: "theme-color" }).setAttribute("content", settings?.visual?.favicon?.theme_color || DEFAULT_SITE_SETTINGS.visual.favicon.theme_color);
  ensureMeta('meta[property="og:type"]', { property: "og:type" }).setAttribute("content", "website");
  ensureMeta('meta[property="og:title"]', { property: "og:title" }).setAttribute("content", title);
  ensureMeta('meta[property="og:description"]', { property: "og:description" }).setAttribute("content", tagline || description);
  ensureMeta('meta[property="og:url"]', { property: "og:url" }).setAttribute("content", url);
  ensureMeta('meta[property="og:image"]', { property: "og:image" }).setAttribute("content", absoluteImage || absoluteFavicon);
  ensureMeta('meta[property="og:image:type"]', { property: "og:image:type" }).setAttribute("content", "image/png");
  ensureMeta('meta[property="og:image:width"]', { property: "og:image:width" }).setAttribute("content", "1200");
  ensureMeta('meta[property="og:image:height"]', { property: "og:image:height" }).setAttribute("content", "630");
  ensureMeta('meta[property="og:image:alt"]', { property: "og:image:alt" }).setAttribute("content", `${companyName} preview image`);
  ensureMeta('meta[property="og:site_name"]', { property: "og:site_name" }).setAttribute("content", companyName);
  ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" }).setAttribute("content", absoluteImage ? "summary_large_image" : "summary");
  ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" }).setAttribute("content", title);
  ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" }).setAttribute("content", tagline || description);
  ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" }).setAttribute("content", absoluteImage || absoluteFavicon);
  ensureMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt" }).setAttribute("content", `${companyName} preview image`);
  ensureMeta('meta[name="twitter:site"]', { name: "twitter:site" }).setAttribute("content", twitterHandle);

  ensureLink('link[rel="icon"]', { rel: "icon", href: absoluteFavicon || favicon }).setAttribute("href", absoluteFavicon || favicon);
  ensureLink('link[rel="apple-touch-icon"]', { rel: "apple-touch-icon", href: toAbsoluteUrl(siteUrl, "/apple-touch-icon.png") }).setAttribute("href", toAbsoluteUrl(siteUrl, "/apple-touch-icon.png"));
  ensureLink('link[rel="canonical"]', { rel: "canonical", href: url }).setAttribute("href", url);
  ensureScript('script[data-seo="structured-data"]', {
    type: "application/ld+json",
    "data-seo": "structured-data",
  }).textContent = JSON.stringify(structuredData);
}

export function SiteSettingsProvider({ children }) {
  const location = useLocation();
  const [serverSettings, setServerSettings] = useState(null);
  const [previewSettings, setPreviewSettings] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      // #region debug-point C:server-settings
      fetch("http://127.0.0.1:7777/event",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({sessionId:"theme-palette-loop",runId:"pre-fix",hypothesisId:"C",location:"SiteSettingsContext.jsx:321",msg:"[DEBUG] fetched server settings",data:{serverPalette:data?.visual?.theme_palette||null,storedPalette:getStoredThemePaletteId(),hasTheme:Boolean(data?.visual?.theme),ts:Date.now()}})}).catch(()=>{});
      // #endregion
      setServerSettings(data || {});
      persistThemePaletteId(data?.visual?.theme_palette);
    } catch {
      setServerSettings({});
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const settings = useMemo(() => {
    const merged = deepMerge(DEFAULT_SITE_SETTINGS, deepMerge(serverSettings || {}, previewSettings || {}));
    const resolved = resolveSettingsWithPalette(
      merged,
      previewSettings?.visual?.theme_palette,
      serverSettings?.visual?.theme_palette,
      getStoredThemePaletteId(),
    );
    // #region debug-point B:resolved-settings
    fetch("http://127.0.0.1:7777/event",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({sessionId:"theme-palette-loop",runId:"pre-fix",hypothesisId:"B",location:"SiteSettingsContext.jsx:337",msg:"[DEBUG] resolved settings palette",data:{previewPalette:previewSettings?.visual?.theme_palette||null,serverPalette:serverSettings?.visual?.theme_palette||null,storedPalette:getStoredThemePaletteId(),resolvedPalette:resolved?.visual?.theme_palette||null,accent:resolved?.visual?.theme?.accent||null,ts:Date.now()}})}).catch(()=>{});
    // #endregion
    return resolved;
  }, [serverSettings, previewSettings]);

  useEffect(() => {
    // #region debug-point E:apply-theme
    fetch("http://127.0.0.1:7777/event",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({sessionId:"theme-palette-loop",runId:"pre-fix",hypothesisId:"E",location:"SiteSettingsContext.jsx:349",msg:"[DEBUG] applying theme and head",data:{pathname:location.pathname,palette:settings?.visual?.theme_palette||null,accent:settings?.visual?.theme?.accent||null,ts:Date.now()}})}).catch(()=>{});
    // #endregion
    applyTheme(settings);
    applyHead(settings, location.pathname);
  }, [settings, location.pathname]);

  const saveSettings = useCallback(async (next) => {
    const { data } = await api.put("/admin/settings", { data: next });
    setPreviewSettings(null);
    setServerSettings(data || {});
    persistThemePaletteId(data?.visual?.theme_palette);
    return data;
  }, []);

  const preview = useCallback((next) => {
    const normalized = next || null;
    setPreviewSettings((current) => (
      stableSettingsSignature(current) === stableSettingsSignature(normalized) ? current : normalized
    ));
  }, []);

  const clearPreview = useCallback(() => {
    setPreviewSettings((current) => (current === null ? current : null));
  }, []);

  return (
    <SiteSettingsContext.Provider
      value={{
        settings,
        loaded,
        saveSettings,
        refreshSettings,
        previewSettings,
        preview,
        clearPreview,
        resolveAssetUrl,
      }}
    >
      {children}
    </SiteSettingsContext.Provider>
  );
}

export const useSiteSettings = () => useContext(SiteSettingsContext);
